import { useState, useEffect, useCallback } from 'react';

// Types for the fetch-wrapper functions
type OnlineStateListener = (online: boolean) => void;
type SubscribeFn = (listener: OnlineStateListener) => () => void;
type GetIsOnlineFn = () => boolean;
type VerifyOnlineStatusFn = () => Promise<boolean>;

// Cached references to fetch-wrapper functions
let _subscribeToOnlineState: SubscribeFn | null = null;
let _getIsOnline: GetIsOnlineFn | null = null;
let _verifyOnlineStatus: VerifyOnlineStatusFn | null = null;
let _loadPromise: Promise<void> | null = null;

// Load the fetch-wrapper functions dynamically
const loadFetchWrapper = async (): Promise<void> => {
  if (_loadPromise) return _loadPromise;

  const isTauri = typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined;
  if (!isTauri) return;

  _loadPromise = import('@readcloud/fetch-wrapper').then((module) => {
    _subscribeToOnlineState = module.subscribeToOnlineState;
    _getIsOnline = module.getIsOnline;
    _verifyOnlineStatus = module.verifyOnlineStatus;
  }).catch(() => {
    // If import fails, we'll fall back to browser events
  });

  return _loadPromise;
};

/**
 * A hook that reliably detects online/offline status.
 *
 * For Tauri apps, this subscribes to the online state managed by tauriFetch,
 * which is updated based on actual API request success/failure.
 *
 * For web apps, it uses navigator.onLine events as they work reliably in browsers.
 */
export const useIsOnline = (): boolean => {
  const isTauri = typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined;

  const [isOnline, setIsOnline] = useState<boolean>(() => {
    // For Tauri, try to get the current state if already loaded
    if (isTauri && _getIsOnline) {
      return _getIsOnline();
    }
    // Otherwise use navigator.onLine as initial value
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true;
  });

  // For Tauri: subscribe to the fetch-wrapper's online state
  useEffect(() => {
    if (!isTauri) return;

    let unsubscribe: (() => void) | null = null;
    let mounted = true;

    loadFetchWrapper().then(() => {
      if (!mounted) return;

      if (_subscribeToOnlineState) {
        unsubscribe = _subscribeToOnlineState((online) => {
          if (mounted) {
            setIsOnline(online);
          }
        });
      }
    });

    // Also listen to browser events as a backup
    const handleOffline = () => {
      if (mounted) setIsOnline(false);
    };

    window.addEventListener('offline', handleOffline);

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
      window.removeEventListener('offline', handleOffline);
    };
  }, [isTauri]);

  // For web apps: use navigator.onLine events
  useEffect(() => {
    if (isTauri) return;

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isTauri]);

  return isOnline;
};

/**
 * Utility function to check if currently online (one-time check).
 * For Tauri, returns the current known state from fetch-wrapper.
 * For web, checks navigator.onLine.
 */
export const checkIsOnline = (): boolean => {
  const isTauri = typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined;

  if (isTauri && _getIsOnline) {
    return _getIsOnline();
  }

  return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

/**
 * Verify online status by making a network request.
 * For Tauri, pings the OIDC endpoint and updates the global online state.
 * For web, returns navigator.onLine (no network request needed).
 * Returns a Promise<boolean> indicating if online.
 */
export const verifyOnlineStatus = async (): Promise<boolean> => {
  const isTauri = typeof window !== 'undefined' && (window as any).__TAURI__ !== undefined;

  if (isTauri) {
    // Ensure fetch-wrapper is loaded
    await loadFetchWrapper();

    if (_verifyOnlineStatus) {
      return _verifyOnlineStatus();
    }
  }

  // For web, navigator.onLine is reliable
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

export default useIsOnline;
